import Matter from 'matter-js';
import { svgPathProperties } from 'svg-path-properties';
import { astroBoy, batman, cn, harryPotter, looneyTunes, lotr, naruto, ppg, sesame, smurfs } from '../atoms/logos';
const interactiveLogos = () => {
    const classes = {
        interactiveLogosSectionHidden: 'interactive-logos--hidden',
    };
    const selectors = {
        interactiveLogosSection: '[data-interactive-logos]',
    };
    const interactiveLogosSections = document.querySelectorAll(selectors.interactiveLogosSection);
    interactiveLogosSections.forEach((section) => {
        if (section.dataset.matterInitialized)
            return;
        section.dataset.matterInitialized = 'true';
        // Create
        const engine = Matter.Engine.create();
        const world = engine.world;
        const render = Matter.Render.create({
            element: section,
            engine: engine,
            options: {
                wireframes: false,
                background: 'transparent',
                width: section.offsetWidth,
                height: section.offsetHeight,
            },
        });
        // Walls
        let topWall;
        let rightWall;
        let bottomWall;
        let leftWall;
        const createWalls = () => {
            const wallThickness = 100;
            const wallOffset = -30;
            const wallOptions = { isStatic: true, render: { visible: false } };
            topWall = Matter.Bodies.rectangle(section.offsetWidth / 2, wallOffset, section.offsetWidth, wallThickness, wallOptions);
            rightWall = Matter.Bodies.rectangle(section.offsetWidth + wallOffset * -1, section.offsetHeight / 2, wallThickness, section.offsetHeight * 1.5, wallOptions);
            bottomWall = Matter.Bodies.rectangle(section.offsetWidth / 2, section.offsetHeight + wallOffset * -1, section.offsetWidth, wallThickness, wallOptions);
            leftWall = Matter.Bodies.rectangle(wallOffset, section.offsetHeight / 2, wallThickness, section.offsetHeight * 1.5, wallOptions);
            Matter.World.add(world, [topWall, rightWall, bottomWall, leftWall]);
        };
        createWalls();
        // Mouse
        const mouse = Matter.Mouse.create(render.canvas);
        const mouseConstraint = Matter.MouseConstraint.create(engine, {
            mouse: mouse,
            constraint: { render: { visible: false } },
        });
        const mouseWheelHandler = (e) => {
            window.scrollBy(0, e.deltaY);
        };
        render.canvas.addEventListener('wheel', mouseWheelHandler);
        // Logos
        const logos = [astroBoy, batman, cn, harryPotter, looneyTunes, lotr, naruto, sesame, smurfs, ppg];
        let logoBodies = [];
        const createLogos = () => {
            // Remove existing logos
            Matter.World.remove(world, logoBodies);
            logoBodies = [];
            logos.forEach((logo, index) => {
                const parser = new DOMParser();
                const svgDoc = parser.parseFromString(logo, 'image/svg+xml');
                const pathElement = svgDoc.querySelector('path');
                if (pathElement) {
                    const pathData = pathElement.getAttribute('d');
                    const logoImage = new Image();
                    const svgBlob = new Blob([logo], { type: 'image/svg+xml;charset=utf-8' });
                    const url = URL.createObjectURL(svgBlob);
                    logoImage.src = url;
                    logoImage.onload = () => {
                        const properties = new svgPathProperties(pathData);
                        const totalLength = properties.getTotalLength();
                        const rawVertices = [];
                        for (let i = 0; i < totalLength; i += 5) {
                            const { x, y } = properties.getPointAtLength(i);
                            rawVertices.push({ x, y });
                        }
                        // Bounds + scale
                        const xs = rawVertices.map((p) => p.x);
                        const ys = rawVertices.map((p) => p.y);
                        const minX = Math.min(...xs);
                        const maxX = Math.max(...xs);
                        const minY = Math.min(...ys);
                        const maxY = Math.max(...ys);
                        const originalWidth = maxX - minX;
                        const targetWidth = section.offsetWidth / 5;
                        const scale = targetWidth / originalWidth;
                        const vertices = rawVertices.map((p) => ({
                            x: (p.x - minX) * scale,
                            y: (p.y - minY) * scale,
                        }));
                        let spriteScale = scale;
                        if (logo === batman) {
                            spriteScale *= 0.9;
                        }
                        if (logo === harryPotter) {
                            spriteScale *= 1.5;
                        }
                        if (logo === looneyTunes) {
                            spriteScale *= 1.25;
                        }
                        if (logo === naruto) {
                            spriteScale *= 0.95;
                        }
                        // Grid
                        const logosPerRow = 5;
                        const logoHeight = 100;
                        const column = (index % logosPerRow) + 1;
                        const row = Math.floor(index / logosPerRow);
                        const xPosition = column * (section.offsetWidth / 6);
                        const yPosition = row * logoHeight;
                        const logoBody = Matter.Bodies.fromVertices(xPosition, yPosition, [vertices], {
                            render: {
                                sprite: {
                                    texture: logoImage.src,
                                    xScale: spriteScale,
                                    yScale: spriteScale,
                                },
                            },
                        }, true);
                        Matter.World.add(world, logoBody);
                        logoBodies.push(logoBody);
                    };
                }
            });
            // Show Section
            section.classList.remove(classes.interactiveLogosSectionHidden);
        };
        // Add Logos when scrolled into view
        const intersectionObserverOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.2,
        };
        const observer = new IntersectionObserver((entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    createLogos();
                    observer.unobserve(entry.target);
                }
            });
        }, intersectionObserverOptions);
        observer.observe(section);
        // Start
        const runner = Matter.Runner.create();
        Matter.World.add(world, [topWall, rightWall, bottomWall, leftWall]);
        Matter.World.add(world, mouseConstraint);
        Matter.Render.run(render);
        Matter.Runner.run(runner, engine);
        // Resize
        const resizeHandler = () => {
            // Update canvas size
            render.canvas.width = section.offsetWidth;
            render.canvas.height = section.offsetHeight;
            render.options.width = section.offsetWidth;
            render.options.height = section.offsetHeight;
            // Walls
            Matter.World.remove(world, [topWall, rightWall, bottomWall, leftWall]);
            Matter.World.remove(world, [topWall, rightWall, bottomWall, leftWall]);
            createWalls();
            // Logos
            createLogos();
        };
        let resizeTimeout;
        window.addEventListener('resize', () => {
            section.classList.add(classes.interactiveLogosSectionHidden);
            Matter.World.remove(world, logoBodies);
            if (resizeTimeout)
                clearTimeout(resizeTimeout);
            resizeTimeout = window.setTimeout(() => {
                resizeHandler();
            }, 200);
        });
    });
};
export { interactiveLogos };
